import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, Field, FormActions, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { clean, formatDate, labelOf, openButtonClass, ProductChoice, ProductPicker, TableFooter, thClass, today, useListState } from './partApprovalShared';

type Status = 'draft' | 'submitted' | 'approved' | 'interim' | 'rejected';
type ElementStatus = 'pending' | 'ready' | 'not_applicable';
const REASONS: Record<string, string> = {
  initial_submission: 'Initial submission',
  engineering_change: 'Engineering change',
  tooling: 'Tooling: transfer, replacement, refurbishment or additional',
  correction_of_discrepancy: 'Correction of discrepancy',
  tooling_inactive: 'Tooling inactive for more than a year',
  process_change: 'Change to the process',
  new_source_or_location: 'Change of source or location',
  other: 'Other',
};
const STATUS: Record<Status, { label: string; className: string }> = {
  draft: { label: 'Being put together', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  submitted: { label: 'With the customer', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  approved: { label: 'Approved', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  interim: { label: 'Interim approval', className: 'bg-purple-50 text-purple-700 ring-purple-600/20' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 ring-red-600/20' },
};

interface PpapRow {
  id: string;
  ppapNumber: string;
  productId: string;
  productName: string;
  sku: string;
  customerId: string;
  customerName: string | null;
  level: number;
  reason: string;
  customerPartNumber: string | null;
  partRevision: string | null;
  firstArticleId: string | null;
  faiNumber: string | null;
  faiStatus: string | null;
  status: Status;
  submissions: number;
  submittedOn: string | null;
  dispositionOn: string | null;
  dispositionNote: string | null;
  customerReference: string | null;
  notes: string | null;
  ready: number;
  pending: number;
  notApplicable: number;
}
interface PpapElement {
  number: number;
  name: string;
  status: ElementStatus;
  reference: string | null;
}
interface Ppap extends PpapRow {
  elements: PpapElement[];
}
interface Choice {
  id: string;
  name: string;
}

const ppapApi = {
  page: async (params: object): Promise<{ data: PpapRow[]; total: number; totalPages: number }> => (await api.get('/ppaps', { params: clean(params) })).data,
  one: async (id: string): Promise<Ppap> => (await api.get(`/ppaps/${id}`)).data,
  save: async (id: string | null, data: object): Promise<Ppap> => (id ? await api.put(`/ppaps/${id}`, data) : await api.post('/ppaps', data)).data,
  saveElements: async (id: string, elements: object[]): Promise<Ppap> => (await api.put(`/ppaps/${id}/elements`, { elements })).data,
  act: async (id: string, action: 'submit' | 'disposition' | 'reopen', data: object = {}): Promise<Ppap> => (await api.post(`/ppaps/${id}/${action}`, data)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/ppaps/${id}`);
  },
  customers: async (): Promise<Choice[]> => {
    const result = (await api.get('/customers', { params: { limit: 100 } })).data;
    return (Array.isArray(result) ? result : result?.data ?? []) as Choice[];
  },
  firstArticles: async (productId: string): Promise<{ id: string; faiNumber: string; status: string; inspectedOn: string }[]> => (await api.get('/first-articles', { params: { productId, limit: 100 } })).data.data,
};

// ---------------------------------------------------------------- start or change a submission

const PpapForm: React.FC<{ ppap: Ppap | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ ppap, isPending, onCancel, onSubmit }) => {
  const { data: customers = [] } = useQuery({ queryKey: ['ppaps', 'customers'], queryFn: ppapApi.customers });
  const [product, setProduct] = useState<ProductChoice | null>(ppap ? { id: ppap.productId, name: ppap.productName, sku: ppap.sku } : null);
  const [form, setForm] = useState({ customerId: ppap?.customerId ?? '', level: String(ppap?.level ?? 3), reason: ppap?.reason ?? 'initial_submission', customerPartNumber: ppap?.customerPartNumber ?? '', partRevision: ppap?.partRevision ?? '', firstArticleId: ppap?.firstArticleId ?? '', notes: ppap?.notes ?? '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { data: firstArticles = [] } = useQuery({ queryKey: ['ppaps', 'first-articles', product?.id], queryFn: () => ppapApi.firstArticles(product?.id as string), enabled: !!product });
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!product) next.product = 'Choose the part';
    if (!form.customerId) next.customerId = 'Choose the customer';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ productId: product?.id, customerId: form.customerId, level: Number(form.level), reason: form.reason, customerPartNumber: form.customerPartNumber.trim() || null, partRevision: form.partRevision.trim() || null, firstArticleId: form.firstArticleId || null, notes: form.notes.trim() || null });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Part" htmlFor="pp-product" required error={errors.product}>
          <ProductPicker id="pp-product" value={product} onChange={(choice) => { setProduct(choice); setForm((prev) => ({ ...prev, firstArticleId: '' })); }} disabled={!!ppap} error={!!errors.product} />
        </Field>
        <Field label="Customer" htmlFor="pp-customer" required error={errors.customerId}>
          <select id="pp-customer" value={form.customerId} disabled={!!ppap} onChange={set('customerId')} className={cn(inputClass, errors.customerId && inputErrorClass)}>
            <option value="">Choose…</option>
            {customers.map((customer) => (<option key={customer.id} value={customer.id}>{customer.name}</option>))}
          </select>
        </Field>
        <Field label="Their part number" htmlFor="pp-part"><input id="pp-part" type="text" maxLength={60} value={form.customerPartNumber} onChange={set('customerPartNumber')} className={inputClass} /></Field>
        <Field label="Revision of the part" htmlFor="pp-revision"><input id="pp-revision" type="text" maxLength={30} value={form.partRevision} onChange={set('partRevision')} className={inputClass} /></Field>
        <Field label="Submission level" htmlFor="pp-level" required hint="As the customer asks; 3 unless they say otherwise">
          <select id="pp-level" value={form.level} onChange={set('level')} className={inputClass}>
            {[1, 2, 3, 4, 5].map((level) => (<option key={level} value={level}>Level {level}</option>))}
          </select>
        </Field>
        <Field label="Why it is submitted" htmlFor="pp-reason" required>
          <select id="pp-reason" value={form.reason} onChange={set('reason')} className={inputClass}>
            {Object.entries(REASONS).map(([value, label]) => (<option key={value} value={value}>{label}</option>))}
          </select>
        </Field>
        <Field label="First article it rests on" htmlFor="pp-fai" hint={product && firstArticles.length === 0 ? 'This part has no first article yet' : undefined}>
          <select id="pp-fai" value={form.firstArticleId} onChange={set('firstArticleId')} disabled={!product} className={inputClass}>
            <option value="">None</option>
            {firstArticles.map((fai) => (<option key={fai.id} value={fai.id}>{fai.faiNumber} · {labelOf(fai.status)} · {formatDate(fai.inspectedOn)}</option>))}
          </select>
        </Field>
        <Field label="Notes" htmlFor="pp-notes"><input id="pp-notes" type="text" maxLength={500} value={form.notes} onChange={set('notes')} className={inputClass} /></Field>
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : ppap ? 'Save Submission' : 'Start Submission'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one submission: its elements and what the customer said

const PpapPanel: React.FC<{ ppapId: string; canEdit: boolean; canDelete: boolean; onEdit: (ppap: Ppap) => void; onClose: () => void; onChanged: () => void }> = ({ ppapId, canEdit, canDelete, onEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: ppap, isLoading, isError, error } = useQuery({ queryKey: ['ppaps', 'one', ppapId], queryFn: () => ppapApi.one(ppapId) });
  const [elements, setElements] = useState<PpapElement[] | null>(null);
  const [deciding, setDeciding] = useState(false);
  const [decision, setDecision] = useState({ disposition: 'approved', note: '', customerReference: '', dispositionOn: today() });
  useEffect(() => {
    if (ppap) setElements(ppap.elements);
  }, [ppap]);
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 8000 });
  const store = (saved: Ppap) => {
    queryClient.setQueryData(['ppaps', 'one', ppapId], saved);
    setElements(saved.elements);
    onChanged();
  };
  const saveElements = useMutation({
    mutationFn: (changed: PpapElement[]) => ppapApi.saveElements(ppapId, changed.map((element) => ({ number: element.number, status: element.status, reference: element.reference || null }))),
    onSuccess: (saved) => {
      toast.success(`${saved.ready} element(s) ready, ${saved.pending} still open`);
      store(saved);
    },
    onError: fail,
  });
  const act = useMutation({
    mutationFn: ({ action, data }: { action: 'submit' | 'disposition' | 'reopen'; data?: object }) => ppapApi.act(ppapId, action, data),
    onSuccess: (saved, { action }) => {
      toast.success(action === 'submit' ? `${saved.ppapNumber} submitted to ${saved.customerName ?? 'the customer'}` : action === 'reopen' ? `${saved.ppapNumber} is open again` : `${saved.ppapNumber}: ${STATUS[saved.status].label.toLowerCase()}`);
      store(saved);
      setDeciding(false);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => ppapApi.remove(ppapId),
    onSuccess: () => {
      toast.success('Submission deleted');
      onChanged();
      onClose();
    },
    onError: fail,
  });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !ppap || !elements) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The submission could not be loaded.')}</p>;
  const draft = ppap.status === 'draft';
  const editable = draft && canEdit;
  const changed = elements.filter((element, index) => element.status !== ppap.elements[index].status || (element.reference ?? '') !== (ppap.elements[index].reference ?? ''));
  const setElement = (number: number, patch: Partial<PpapElement>) => setElements((prev) => (prev ?? []).map((element) => (element.number === number ? { ...element, ...patch } : element)));
  const needsNote = decision.disposition !== 'approved';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, STATUS[ppap.status].className)}>{STATUS[ppap.status].label}</span>
        <span className="font-semibold text-gray-900">{ppap.productName} <span className="font-normal text-gray-500">{ppap.sku}</span></span>
        <span>for {ppap.customerName ?? '—'}{ppap.customerPartNumber ? ` (their part ${ppap.customerPartNumber})` : ''}</span>
        <span>Level {ppap.level}</span>
        {ppap.partRevision && <span>Revision {ppap.partRevision}</span>}
        <span>{REASONS[ppap.reason] ?? labelOf(ppap.reason)}</span>
        {ppap.faiNumber && <span>First article {ppap.faiNumber} ({labelOf(ppap.faiStatus ?? '')})</span>}
      </div>
      {ppap.submittedOn && (
        <p className={cn('rounded-md px-4 py-3 text-sm ring-1', ppap.status === 'rejected' ? 'bg-red-50 text-red-800 ring-red-600/20' : ppap.status === 'submitted' ? 'bg-blue-50 text-blue-800 ring-blue-600/20' : 'bg-gray-50 text-gray-700 ring-gray-200')}>
          Submitted {formatDate(ppap.submittedOn)}{ppap.submissions > 1 ? `, for the ${ppap.submissions === 2 ? 'second' : `${ppap.submissions}th`} time` : ''}.
          {ppap.dispositionOn && ` The customer answered on ${formatDate(ppap.dispositionOn)}${ppap.customerReference ? ` under ${ppap.customerReference}` : ''}${ppap.dispositionNote ? `: ${ppap.dispositionNote}` : '.'}`}
        </p>
      )}

      <section className="rounded-lg border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">The eighteen elements · {ppap.ready} ready · {ppap.notApplicable} not applicable · {ppap.pending} open</h4>
          {editable && changed.length > 0 && (
            <button type="button" onClick={() => saveElements.mutate(changed)} disabled={saveElements.isPending} className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50">
              {saveElements.isPending ? 'Saving…' : `Save ${changed.length} Change(s)`}
            </button>
          )}
        </div>
        <ul className="divide-y divide-gray-100">
          {elements.map((element) => (
            <li key={element.number} className="flex flex-wrap items-center gap-3 px-3 py-1.5">
              <span className="w-6 text-sm text-gray-500">{element.number}</span>
              <span className="min-w-[14rem] flex-1 text-sm text-gray-900">{element.name}</span>
              {editable ? (
                <>
                  <select value={element.status} onChange={(event) => setElement(element.number, { status: event.target.value as ElementStatus })} aria-label={`Status of ${element.name}`} className={cn(inputClass, 'w-40 py-1', element.status === 'pending' && 'text-amber-700')}>
                    <option value="pending">Open</option>
                    <option value="ready">Ready</option>
                    <option value="not_applicable">Not applicable</option>
                  </select>
                  <input type="text" maxLength={200} value={element.reference ?? ''} onChange={(event) => setElement(element.number, { reference: event.target.value })} placeholder="Where the evidence is" aria-label={`Evidence for ${element.name}`} className={cn(inputClass, 'w-64 py-1')} />
                </>
              ) : (
                <>
                  <span className={cn(badgeClass, element.status === 'ready' ? 'bg-green-50 text-green-700 ring-green-600/20' : element.status === 'pending' ? 'bg-amber-50 text-amber-800 ring-amber-600/20' : 'bg-gray-50 text-gray-500 ring-gray-500/20')}>{element.status === 'ready' ? 'Ready' : element.status === 'pending' ? 'Open' : 'Not applicable'}</span>
                  <span className="w-64 truncate text-sm text-gray-600">{element.reference || '—'}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      {deciding ? (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="What the customer decided" htmlFor="pd-disposition" required>
              <select id="pd-disposition" value={decision.disposition} onChange={(event) => setDecision({ ...decision, disposition: event.target.value })} className={inputClass}>
                <option value="approved">Approved</option>
                <option value="interim">Interim approval</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>
            <Field label="On" htmlFor="pd-date" required><input id="pd-date" type="date" min={ppap.submittedOn ?? undefined} max={today()} value={decision.dispositionOn} onChange={(event) => setDecision({ ...decision, dispositionOn: event.target.value })} className={inputClass} /></Field>
            <Field label="Their approval number" htmlFor="pd-reference"><input id="pd-reference" type="text" maxLength={80} value={decision.customerReference} onChange={(event) => setDecision({ ...decision, customerReference: event.target.value })} className={inputClass} /></Field>
            <Field label={decision.disposition === 'interim' ? 'On what conditions' : decision.disposition === 'rejected' ? 'Why' : 'Remarks'} htmlFor="pd-note" required={needsNote} className="sm:col-span-3">
              <input id="pd-note" type="text" maxLength={500} value={decision.note} onChange={(event) => setDecision({ ...decision, note: event.target.value })} className={inputClass} />
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setDeciding(false)} className={secondaryButtonClass}>Back</button>
            <button type="button" disabled={act.isPending || !decision.dispositionOn || (needsNote && decision.note.trim().length < 3)} onClick={() => act.mutate({ action: 'disposition', data: { disposition: decision.disposition, note: decision.note.trim() || null, customerReference: decision.customerReference.trim() || null, dispositionOn: decision.dispositionOn } })} className={primaryButtonClass}>
              Record Decision
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-3">
          {draft && ppap.pending > 0 && <span className="mr-auto text-xs text-gray-500">{ppap.pending} element(s) still open; each has to be ready or not applicable before it is submitted</span>}
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {draft && canDelete && ppap.submissions === 0 && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>}
          {draft && canEdit && <button type="button" onClick={() => onEdit(ppap)} className={secondaryButtonClass}>Edit</button>}
          {draft && canEdit && <button type="button" onClick={() => act.mutate({ action: 'submit' })} disabled={act.isPending || changed.length > 0} title={changed.length > 0 ? 'Save the changes to the elements first' : undefined} className={primaryButtonClass}>Submit to the Customer</button>}
          {ppap.status === 'submitted' && canEdit && <button type="button" onClick={() => { setDecision({ disposition: 'approved', note: '', customerReference: ppap.customerReference ?? '', dispositionOn: today() }); setDeciding(true); }} className={primaryButtonClass}>Record the Customer's Decision</button>}
          {(ppap.status === 'interim' || ppap.status === 'rejected') && canEdit && <button type="button" onClick={() => act.mutate({ action: 'reopen' })} disabled={act.isPending} className={primaryButtonClass}>Work on It Again</button>}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the tab

export const Ppaps: React.FC<{ canCreate: boolean; canEdit: boolean; canDelete: boolean; onChanged: () => void }> = ({ canCreate, canEdit, canDelete, onChanged }) => {
  const queryClient = useQueryClient();
  const list = useListState();
  const [status, setStatus] = useState<Status | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Ppap | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['ppaps', 'page', { page: list.page, limit: list.limit, search: list.search, status }],
    queryFn: () => ppapApi.page({ page: list.page, limit: list.limit, search: list.search, status }),
    placeholderData: keepPreviousData,
  });
  const { data: viewing } = useQuery({ queryKey: ['ppaps', 'one', viewingId], queryFn: () => ppapApi.one(viewingId as string), enabled: !!viewingId });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['ppaps', 'page'] });
    onChanged();
  };
  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };
  const save = useMutation({
    mutationFn: (input: object) => ppapApi.save(editing?.id ?? null, input),
    onSuccess: (ppap) => {
      toast.success(editing ? `${ppap.ppapNumber} saved` : `${ppap.ppapNumber} started`);
      queryClient.setQueryData(['ppaps', 'one', ppap.id], ppap);
      refresh();
      closeForm();
      setViewingId(ppap.id);
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The submission could not be saved'), { duration: 7000 }),
  });
  const rows = data?.data ?? [];

  return (
    <>
      <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="search" value={list.searchInput} onChange={(event) => list.setSearchInput(event.target.value)} placeholder="Search by number, part, customer or their part number" aria-label="Search PPAP submissions" className={cn(inputClass, 'pl-9')} />
        </div>
        <select value={status} onChange={(event) => { setStatus(event.target.value as Status | ''); list.setPage(1); }} aria-label="PPAP status" className={cn(inputClass, 'lg:w-52')}>
          <option value="">All</option>
          {(Object.keys(STATUS) as Status[]).map((value) => (<option key={value} value={value}>{STATUS[value].label}</option>))}
        </select>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            New PPAP Submission
          </button>
        )}
      </div>
      <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Submission</th>
              <th scope="col" className={thClass}>Part</th>
              <th scope="col" className={thClass}>Customer</th>
              <th scope="col" className={thClass}>Level</th>
              <th scope="col" className={thClass}>Elements</th>
              <th scope="col" className={thClass}>Status</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isError ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The submissions could not be loaded.')}</td></tr>
            ) : isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <p className="text-sm font-medium text-gray-900">{list.search || status ? 'No submission matches' : 'No PPAP submissions yet'}</p>
                  <p className="mt-1 text-sm text-gray-500">Start a submission when a customer asks for production part approval of a new or changed part.</p>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-4 py-3">
                    <button type="button" onClick={() => setViewingId(row.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{row.ppapNumber}</button>
                    <p className="mt-0.5 text-xs text-gray-500">{row.submittedOn ? `Submitted ${formatDate(row.submittedOn)}` : 'Not yet submitted'}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{row.productName}<span className="block text-xs text-gray-500">{[row.sku, row.partRevision && `rev ${row.partRevision}`].filter(Boolean).join(' · ')}</span></td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.customerName ?? '—'}{row.customerPartNumber && <span className="block text-xs text-gray-500">{row.customerPartNumber}</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.level}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 tabular-nums">{row.ready + row.notApplicable} of 18{row.pending > 0 && <span className="block text-xs text-amber-700">{row.pending} open</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                  <td className="whitespace-nowrap px-4 py-3 text-right"><button type="button" onClick={() => setViewingId(row.id)} className={openButtonClass} aria-label={`Open ${row.ppapNumber}`}>Open</button></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <TableFooter page={list.page} limit={list.limit} total={data?.total ?? 0} totalPages={data?.totalPages ?? 1} onPage={list.setPage} onLimit={list.setLimit} />

      <Modal isOpen={formOpen || !!viewingId} onClose={() => (formOpen ? closeForm() : setViewingId(null))} title={formOpen ? (editing ? `Edit ${editing.ppapNumber}` : 'New PPAP Submission') : viewing?.ppapNumber ?? 'PPAP Submission'} size="xl">
        {formOpen ? (
          <PpapForm key={editing?.id ?? 'new'} ppap={editing} isPending={save.isPending} onCancel={closeForm} onSubmit={(input) => save.mutate(input)} />
        ) : (
          viewingId && <PpapPanel key={viewingId} ppapId={viewingId} canEdit={canEdit} canDelete={canDelete} onEdit={(ppap) => { setEditing(ppap); setFormOpen(true); }} onClose={() => setViewingId(null)} onChanged={refresh} />
        )}
      </Modal>
    </>
  );
};
